import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router";
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  createFilterOptions,
  Paper,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Divider,
  Chip,
  Collapse,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "../store";
import { getVehicleList } from "../store/slices/vehicleSlice";
import { getAuthState } from "../store/slices/authSlice";
import { formatDate } from "../utils/helper";
import { VehicleOption } from "../utils/types";
import useHandleRefresh from "../hooks/useHandleRefresh";
import { setLoader } from "../store/slices/loaderSlice";
import Api from "../services/apis/setup";

// Service types
const SERVICE_TYPES = [
  'Oil Change',
  'Filter Replacement',
  'Air Filter',
  'Brake Service',
  'Tyre Service',
  'Battery Service',
  'General Service',
  'Repair',
  'Wheel Alignment',
  'Cooling System',
  'Electrical',
  'Suspension',
  'Transmission',
  'Other'
];

// Single service item type
type ServiceItem = {
  id: string;
  serviceType: string;
  partName: string;
  partQuantity: number;
  unitPrice: number;
  totalPartCost: number;
  serviceDescription: string;
};

// Status color mapping
const getStatusColor = (status: string) => {
  switch (status) {
    case 'closed': return 'success';
    case 'payment': return 'warning';
    case 'review': return 'info';
    case 'processing': return 'primary';
    case 'processed': return 'success';
    case 'consolidating': return 'warning';
    default: return 'default';
  }
};

type Props = {};

function ServiceHistoryDetails({ }: Props) {
  const { id } = useParams<{ id: string }>();
  const vehicleList = useAppSelector(getVehicleList);
  const { user } = useAppSelector(getAuthState);
  const [selectedVehicle, setSelectedVehicle] = useState<any>(null);
  const dispatch = useAppDispatch();
  const [fetchAllVehicles] = Api.useLazyFetchAllVehiclesQuery();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleRefresh = useHandleRefresh((arg: boolean) => dispatch(setLoader(arg)));

  // API hooks for service operations
  const [updateServiceHistoryApi] = Api.useUpdateServiceHistoryMutation();
  const [deleteServiceHistoryApi] = Api.useDeleteServiceHistoryMutation();

  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingServiceData, setEditingServiceData] = useState<any>({});
  const [editServiceItems, setEditServiceItems] = useState<ServiceItem[]>([]);
  const editServiceItemsRef = useRef<ServiceItem[]>([]);

  // Expanded row state
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  // Sync ref with state
  useEffect(() => {
    editServiceItemsRef.current = editServiceItems;
  }, [editServiceItems]);

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Filter state
  const [searchFilter, setSearchFilter] = useState('');

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState<any>(null);

  // Fetch vehicle directly when id changes
  useEffect(() => {
    if (id && user) {
      const fetchVehicle = async () => {
        try {
          const result: any = await fetchAllVehicles({
            baseURL: `/vehicles/${id}`,
          });

          const vehicleData = result?.data?.data || result?.data;
          if (vehicleData) {
            setSelectedVehicle(vehicleData);
          }
        } catch (error) {
          console.error("Error fetching vehicle:", error);
        }
      };
      fetchVehicle();
    }
  }, [id, user]);

  useEffect(() => {
    if (selectedVehicle === null) handleRefresh();
  }, [selectedVehicle, handleRefresh]);

  // Generate unique ID for service items
  const generateId = () => Math.random().toString(36).substr(2, 9);

  // Get service types from service items
  const getServiceTypes = (service: any) => {
    if (service.serviceItems && service.serviceItems.length > 0) {
      return service.serviceItems.map((item: any) => item.serviceType || item.partName).join(', ');
    }
    return service.serviceType || service.partDetails || 'N/A';
  };

  // Handle expand/collapse row
  const handleExpandRow = (serviceId: string) => {
    setExpandedRow(expandedRow === serviceId ? null : serviceId);
  };

  // Handle edit button click - opens modal
  const handleEditClick = (service: any) => {
    setEditingServiceData({
      ...service,
      odo: service.odoReading || '',
      labourCost: service.labourCost || '0',
      nextServiceDueDate: service.nextServiceDueDate || '',
    });
    setEditModalOpen(true);

    const existingItems = service.serviceItems && service.serviceItems.length > 0
      ? service.serviceItems.map((item: any) => ({
          id: item._id || generateId(),
          serviceType: item.serviceType || 'General Service',
          partName: item.partName || '',
          partQuantity: item.partQuantity || 1,
          unitPrice: item.unitPrice || 0,
          totalPartCost: item.totalPartCost || 0,
          serviceDescription: item.serviceDescription || ''
        }))
      : [{
          id: generateId(),
          serviceType: service.serviceType || service.partDetails || 'General Service',
          partName: '',
          partQuantity: 1,
          unitPrice: 0,
          totalPartCost: 0,
          serviceDescription: ''
        }];

    setEditServiceItems(existingItems);
  };

  // Close edit modal
  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setEditingServiceData({});
    setEditServiceItems([]);
  };

  // Add new service item in edit mode
  const addEditServiceItem = () => {
    const newItem: ServiceItem = {
      id: generateId(),
      serviceType: 'General Service',
      partName: '',
      partQuantity: 1,
      unitPrice: 0,
      totalPartCost: 0,
      serviceDescription: ''
    };
    setEditServiceItems([...editServiceItems, newItem]);
  };

  // Remove service item in edit mode
  const removeEditServiceItem = (id: string) => {
    setEditServiceItems(editServiceItems.filter(item => item.id !== id));
  };

  // Update service item in edit mode
  const updateEditServiceItem = (id: string, field: keyof ServiceItem, value: any) => {
    const updatedItems = editServiceItems.map(item => {
      if (item.id === id) {
        const updated = { ...item, [field]: value };
        if (field === 'partQuantity' || field === 'unitPrice') {
          updated.totalPartCost = (updated.partQuantity || 0) * (updated.unitPrice || 0);
        }
        return updated;
      }
      return item;
    });
    setEditServiceItems(updatedItems);
  };

  // Calculate total service cost
  const calculateEditTotalCost = () => {
    const partsCost = editServiceItems.reduce((sum, item) => sum + (item.totalPartCost || 0), 0);
    const labour = parseFloat(editingServiceData.labourCost) || 0;
    return partsCost + labour;
  };

  // Handle save edit
  const handleSaveEdit = async () => {
    const currentItems = editServiceItemsRef.current;
    try {
      const totalPartsCost = currentItems.reduce((sum, item) => sum + (item.totalPartCost || 0), 0);
      const labourCost = parseFloat(editingServiceData.labourCost) || 0;
      const totalServiceCost = totalPartsCost + labourCost;

      const updatedData = {
        spareChangedDate: editingServiceData.spareChangedDate,
        serviceDate: editingServiceData.spareChangedDate,
        serviceItems: currentItems.map(item => ({
          serviceType: item.serviceType,
          partName: item.partName,
          partQuantity: item.partQuantity,
          unitPrice: item.unitPrice,
          totalPartCost: item.totalPartCost,
          serviceDescription: item.serviceDescription
        })),
        totalPartsCost,
        labourCost,
        totalServiceCost,
        partDetails: currentItems[0]?.partName || editingServiceData.partDetails,
        amountOfParts: currentItems.reduce((sum, item) => sum + item.partQuantity, 0),
        description: currentItems.map(item => item.serviceDescription).join(', '),
        sparePreviousModifiedDate: editingServiceData.sparePreviousModifiedDate,
        nextOdoCheckPoint: Number(editingServiceData.nextOdoCheckPoint) || 0,
        changedBy: editingServiceData.mechanicName || editingServiceData.changedBy,
        remarks: editingServiceData.remarks,
        status: editingServiceData.status,
        odoReading: Number(editingServiceData.odo),
        nextServiceDueKm: Number(editingServiceData.nextServiceDueKm) || 0,
        nextServiceDueDate: editingServiceData.nextServiceDueDate,
        mechanicName: editingServiceData.mechanicName,
        serviceCenter: editingServiceData.serviceCenter
      };

      await updateServiceHistoryApi({
        baseURL: `/vehicles/${selectedVehicle._id}/service/${editingServiceData._id}`,
        method: 'PUT',
        payload: JSON.stringify(updatedData),
        arguments: {
          vehicleId: selectedVehicle._id,
          serviceId: editingServiceData._id,
          data: updatedData
        }
      }).unwrap();

      const updatedHistory = selectedVehicle.history.map((service: any) =>
        service._id === editingServiceData._id ? updatedData : service
      );

      setSelectedVehicle({
        ...selectedVehicle,
        history: updatedHistory
      });

      handleCloseEditModal();
      setSnackbar({
        open: true,
        message: 'Service updated successfully!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error updating service:', error);
      setSnackbar({
        open: true,
        message: 'Error updating service. Please try again.',
        severity: 'error'
      });
    }
  };

  // Handle delete service
  const handleDeleteClick = (service: any) => {
    setServiceToDelete(service);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteServiceHistoryApi({
        baseURL: `/vehicles/${selectedVehicle._id}/service/${serviceToDelete._id}`,
        method: 'DELETE',
        arguments: {
          vehicleId: selectedVehicle._id,
          serviceId: serviceToDelete._id
        }
      } as any).unwrap();

      const updatedHistory = selectedVehicle.history.filter((service: any) =>
        service._id !== serviceToDelete._id
      );

      setSelectedVehicle({
        ...selectedVehicle,
        history: updatedHistory
      });

      setDeleteDialogOpen(false);
      setServiceToDelete(null);
      setSnackbar({
        open: true,
        message: 'Service deleted successfully!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error deleting service:', error);
      setSnackbar({
        open: true,
        message: 'Error deleting service. Please try again.',
        severity: 'error'
      });
    }
  };

  // Filter services based on search
  const filteredServices = selectedVehicle?.history?.filter((service: any) => {
    // Skip entries without valid _id
    if (!service?._id) return false;
    if (!searchFilter) return true;
    const filterLower = searchFilter.toLowerCase();
    return (
      service.partDetails?.toLowerCase().includes(filterLower) ||
      service.description?.toLowerCase().includes(filterLower) ||
      service.changedBy?.toLowerCase().includes(filterLower) ||
      service.mechanicName?.toLowerCase().includes(filterLower) ||
      service.serviceCenter?.toLowerCase().includes(filterLower) ||
      formatDate(service.spareChangedDate)?.toLowerCase().includes(filterLower)
    );
  }) || [];

  // Early return for loading state
  if (selectedVehicle === null) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">
          Vehicle not found.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f7fa', minHeight: '100vh' }}>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#062141', mb: 1 }}>
          Service History Details
        </Typography>
        <Typography variant="body2" sx={{ color: '#666' }}>
          View and manage all service records for your vehicles
        </Typography>
      </Box>

      {/* Vehicle Selection Section */}
      <Box sx={{ mb: 4 }}>
        <Paper sx={{ p: 3, backgroundColor: 'white', borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#062141', mb: 2 }}>
            Select Vehicle
          </Typography>
          <Autocomplete
            options={vehicleList.map((v) => ({
              id: v._id,
              number: v.number,
              chasisNumber: v.transport?.chasisNumber?.toString() ?? "",
            }))}
            getOptionLabel={(option) => option.number}
            filterOptions={createFilterOptions<VehicleOption>({
              stringify: (option) => `${option.number} ${option.chasisNumber ?? ""}`,
            })}
            value={selectedVehicle}
            onChange={(_, value) => {
              const vehicle = vehicleList.find((v: any) => v._id === value?.id);
              if (vehicle) setSelectedVehicle(vehicle);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search & Select Vehicle"
                placeholder="Type vehicle number or chassis number"
                required
                fullWidth
                variant="outlined"
              />
            )}
            sx={{ backgroundColor: "white" }}
          />
        </Paper>
      </Box>

      {selectedVehicle && (
        <>
          {/* Vehicle Information Section */}
          <Box sx={{ mb: 4 }}>
            <Paper sx={{ p: 3, backgroundColor: 'white', borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#062141', mb: 3, pb: 2, borderBottom: '2px solid #e0e0e0' }}>
                Vehicle Information
              </Typography>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#999', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Transport Name
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500, color: '#062141', mt: 0.5 }}>
                        {selectedVehicle?.transport?.transportName || 'N/A'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#999', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Registration Number
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500, color: '#062141', mt: 0.5 }}>
                        {selectedVehicle?.transport?.registrationNumber || 'N/A'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#999', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Chassis Number
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500, color: '#062141', mt: 0.5 }}>
                        {selectedVehicle?.transport?.chasisNumber || 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#999', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Engine Number
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500, color: '#062141', mt: 0.5 }}>
                        {selectedVehicle?.transport?.engineNumber || 'N/A'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#999', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Owner
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500, color: '#062141', mt: 0.5 }}>
                        {selectedVehicle?.transport?.details?.owner || 'N/A'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#999', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Contact
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500, color: '#062141', mt: 0.5 }}>
                        {selectedVehicle?.transport?.details?.contact || 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Box>

          {/* Service History Section */}
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#062141', mb: 2, pb: 1, borderBottom: '2px solid #e0e0e0' }}>
              Service History ({selectedVehicle?.history?.length || 0})
            </Typography>

            {/* Search Filter */}
            {selectedVehicle?.history && selectedVehicle.history.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  label="Filter Services"
                  placeholder="Search by part details, description, or person name..."
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  variant="outlined"
                  sx={{
                    maxWidth: 500,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'white',
                    },
                  }}
                />
              </Box>
            )}

            {/* Services Table with Expandable Rows - Desktop */}
            {!isMobile && selectedVehicle?.history && selectedVehicle.history.length > 0 ? (
              <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#062141' }}>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }}>Service Type</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }}>Date</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }}>Total Cost</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }}>Mechanic</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600, width: 120 }} align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredServices.map((service: any) => (
                      <React.Fragment key={service._id}>
                        {/* Main Row */}
                        <TableRow
                          sx={{
                            '&:nth-of-type(even)': { backgroundColor: '#f8f9fa' },
                            '&:hover': { backgroundColor: '#e3f2fd' },
                            cursor: 'pointer',
                          }}
                        >
                          <TableCell component="th" scope="row" sx={{ fontWeight: 500 }} onClick={() => handleExpandRow(service._id)}>
                            {getServiceTypes(service)}
                          </TableCell>
                          <TableCell onClick={() => handleExpandRow(service._id)}>{formatDate(service.spareChangedDate) || 'N/A'}</TableCell>
                          <TableCell onClick={() => handleExpandRow(service._id)} sx={{ fontWeight: 600 }}>₹{(service.totalServiceCost || service.amountOfParts || 0).toFixed(2)}</TableCell>
                          <TableCell onClick={() => handleExpandRow(service._id)}>{service.mechanicName || service.changedBy || 'N/A'}</TableCell>
                          <TableCell onClick={() => handleExpandRow(service._id)}>
                            <Chip
                              label={service.status || 'initiated'}
                              color={getStatusColor(service.status)}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.5 }}>
                              <Tooltip title={expandedRow === service._id ? 'Collapse' : 'Expand'}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleExpandRow(service._id)}
                                  sx={{ color: '#1976d2' }}
                                >
                                  {expandedRow === service._id ? (
                                    <KeyboardArrowUp />
                                  ) : (
                                    <KeyboardArrowDown />
                                  )}
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Edit Service">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditClick(service)}
                                  sx={{ color: '#4caf50' }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete Service">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteClick(service)}
                                  sx={{ color: '#d32f2f' }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>

                        {/* Expanded Row Details - Only render when expanded */}
                        {expandedRow === service._id && (
                          <TableRow>
                            <TableCell colSpan={6} sx={{ py: 0, border: 0 }}>
                              <Collapse in={expandedRow === service._id} timeout="auto" unmountOnExit>
                                <Box sx={{ py: 3, px: 2 }}>
                                <Grid container spacing={3}>
                                  {/* Service Items */}
                                  {service.serviceItems && service.serviceItems.length > 0 && (
                                    <Grid size={{ xs: 12 }}>
                                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#062141', mb: 2 }}>
                                        Service Items / Parts
                                      </Typography>
                                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                        {service.serviceItems.map((item: any, idx: number) => (
                                          <Chip
                                            key={idx}
                                            label={`${item.partName || item.serviceType} x${item.partQuantity} = ₹${(item.totalPartCost || 0).toFixed(2)}`}
                                            variant="outlined"
                                            sx={{ fontWeight: 500 }}
                                          />
                                        ))}
                                      </Box>
                                    </Grid>
                                  )}

                                  {/* Cost Details */}
                                  <Grid size={{ xs: 12, sm: 4 }}>
                                    <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                                      <Typography variant="caption" color="textSecondary">Parts Cost</Typography>
                                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        ₹{(service.totalPartsCost || 0).toFixed(2)}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                  <Grid size={{ xs: 12, sm: 4 }}>
                                    <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                                      <Typography variant="caption" color="textSecondary">Labour Cost</Typography>
                                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        ₹{(service.labourCost || 0).toFixed(2)}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                  <Grid size={{ xs: 12, sm: 4 }}>
                                    <Box sx={{ p: 2, bgcolor: '#e3f2fd', borderRadius: 1 }}>
                                      <Typography variant="caption" color="primary">Total Cost</Typography>
                                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2' }}>
                                        ₹{(service.totalServiceCost || service.amountOfParts || 0).toFixed(2)}
                                      </Typography>
                                    </Box>
                                  </Grid>

                                  {/* Additional Details */}
                                  <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography variant="caption" color="textSecondary">ODO Reading</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                      {service.odoReading?.toLocaleString() || 'N/A'}
                                    </Typography>
                                  </Grid>
                                  <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography variant="caption" color="textSecondary">Service Center</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                      {service.serviceCenter || 'N/A'}
                                    </Typography>
                                  </Grid>
                                  <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography variant="caption" color="textSecondary">Next Service Due</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                      {service.nextServiceDueKm ? `${service.nextServiceDueKm.toLocaleString()} km` :
                                       service.nextServiceDueDate ? formatDate(service.nextServiceDueDate) : 'N/A'}
                                    </Typography>
                                  </Grid>
                                  <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography variant="caption" color="textSecondary">Description</Typography>
                                    <Typography variant="body1">
                                      {service.description || 'N/A'}
                                    </Typography>
                                  </Grid>

                                  {/* Remarks */}
                                  {service.remarks && (
                                    <Grid size={{ xs: 12 }}>
                                      <Typography variant="caption" color="textSecondary">Remarks</Typography>
                                      <Typography variant="body1">{service.remarks}</Typography>
                                    </Grid>
                                  )}
                                </Grid>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                        )}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : isMobile && selectedVehicle?.history && selectedVehicle.history.length > 0 ? (
              /* Mobile card view */
              <Box sx={{ width: '100%' }}>
                {filteredServices.map((service: any) => (
                  <Paper
                    key={service._id}
                    sx={{
                      mb: 2,
                      p: 2,
                      borderRadius: 2,
                      boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#062141' }}>
                          {getServiceTypes(service)}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {formatDate(service.spareChangedDate) || 'N/A'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                        <Chip
                          label={service.status || 'initiated'}
                          color={getStatusColor(service.status)}
                          size="small"
                          variant="outlined"
                        />
                        <Tooltip title="Edit Service">
                          <IconButton
                            size="small"
                            onClick={() => handleEditClick(service)}
                            sx={{ color: '#4caf50' }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Service">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(service)}
                            sx={{ color: '#d32f2f' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>

                    {/* Service Items */}
                    {service.serviceItems && service.serviceItems.length > 0 && (
                      <Box sx={{ mb: 2, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#666' }}>Parts:</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                          {service.serviceItems.map((item: any, idx: number) => (
                            <Chip
                              key={idx}
                              label={`${item.partName || item.serviceType} x${item.partQuantity}`}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      </Box>
                    )}

                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, fontSize: '0.875rem' }}>
                      <Box>
                        <Typography variant="caption" color="textSecondary">Total Cost</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>₹{(service.totalServiceCost || service.amountOfParts || 0).toFixed(2)}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="textSecondary">ODO</Typography>
                        <Typography variant="body2">{service.odoReading?.toLocaleString() || 'N/A'}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="textSecondary">Mechanic</Typography>
                        <Typography variant="body2">{service.mechanicName || service.changedBy || 'N/A'}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="textSecondary">Service Center</Typography>
                        <Typography variant="body2">{service.serviceCenter || 'N/A'}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="textSecondary">Next Service</Typography>
                        <Typography variant="body2">
                          {service.nextServiceDueKm ? `${service.nextServiceDueKm.toLocaleString()} km` :
                           service.nextServiceDueDate ? formatDate(service.nextServiceDueDate) : 'N/A'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="textSecondary">Parts Cost</Typography>
                        <Typography variant="body2">₹{(service.totalPartsCost || 0).toFixed(2)}</Typography>
                      </Box>
                    </Box>

                    {service.remarks && (
                      <Box sx={{ mt: 2, pt: 1, borderTop: '1px solid #e0e0e0' }}>
                        <Typography variant="caption" color="textSecondary">Remarks:</Typography>
                        <Typography variant="body2">{service.remarks}</Typography>
                      </Box>
                    )}
                  </Paper>
                ))}
              </Box>
            ) : (
              <Alert
                severity="info"
                sx={{
                  backgroundColor: '#e3f2fd',
                  color: '#062141',
                  borderLeft: '4px solid #062141',
                  borderRadius: 1,
                  fontWeight: 500
                }}
              >
                No service history found for this vehicle.
              </Alert>
            )}
          </Box>
        </>
      )}

      {/* Edit Service Modal */}
      <Dialog
        open={editModalOpen}
        onClose={handleCloseEditModal}
        maxWidth="md"
        fullWidth
        aria-labelledby="edit-service-dialog-title"
      >
        <DialogTitle id="edit-service-dialog-title" sx={{ backgroundColor: '#062141', color: 'white', fontWeight: 600 }}>
          Edit Service
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid container spacing={3}>
              {/* Vehicle Information Section */}
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'var(--primary-color)', mb: 2, pb: 1, borderBottom: '1px solid #e0e0e0' }}>
                  Vehicle Information
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Current ODO Reading"
                  type="number"
                  value={editingServiceData.odo || ''}
                  onChange={(e) => setEditingServiceData({
                    ...editingServiceData,
                    odo: e.target.value
                  })}
                  fullWidth
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <DatePicker
                  label="Service Date"
                  value={editingServiceData.spareChangedDate ? new Date(editingServiceData.spareChangedDate) : null}
                  onChange={(date) => setEditingServiceData({
                    ...editingServiceData,
                    spareChangedDate: date ? date.toISOString().split('T')[0] : ''
                  })}
                  slotProps={{
                    textField: { fullWidth: true, variant: 'outlined' }
                  }}
                />
              </Grid>

              {/* Service Items Section */}
              <Grid size={{ xs: 12 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, pb: 1, borderBottom: '1px solid #e0e0e0' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'var(--primary-color)' }}>
                    Service Items
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={addEditServiceItem}
                    size="small"
                    sx={{ backgroundColor: 'var(--primary-color)' }}
                  >
                    Add Service Item
                  </Button>
                </Box>
              </Grid>

              {editServiceItems.length === 0 ? (
                <Grid size={{ xs: 12 }}>
                  <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 2 }}>
                    No service items added. Click "Add Service Item" to add parts/services.
                  </Typography>
                </Grid>
              ) : (
                editServiceItems.map((item, index) => (
                  <Grid size={{ xs: 12 }} key={item.id}>
                    <Paper variant="outlined" sx={{ p: 2, mb: 1, backgroundColor: '#f8f9fa' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          Service Item #{index + 1}
                        </Typography>
                        <IconButton size="small" onClick={() => removeEditServiceItem(item.id)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Service Type</InputLabel>
                            <Select
                              value={item.serviceType}
                              label="Service Type"
                              onChange={(e) => updateEditServiceItem(item.id, 'serviceType', e.target.value)}
                            >
                              {SERVICE_TYPES.map(type => (
                                <MenuItem key={type} value={type}>{type}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <TextField
                            label="Part Name"
                            value={item.partName}
                            onChange={(e) => updateEditServiceItem(item.id, 'partName', e.target.value)}
                            fullWidth
                            size="small"
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <TextField
                            label="Quantity"
                            type="number"
                            value={item.partQuantity}
                            onChange={(e) => updateEditServiceItem(item.id, 'partQuantity', parseInt(e.target.value) || 0)}
                            fullWidth
                            size="small"
                            inputProps={{ min: 1 }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <TextField
                            label="Unit Price (₹)"
                            type="number"
                            value={item.unitPrice}
                            onChange={(e) => updateEditServiceItem(item.id, 'unitPrice', parseFloat(e.target.value) || 0)}
                            fullWidth
                            size="small"
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <TextField
                            label="Total (₹)"
                            value={item.totalPartCost}
                            fullWidth
                            size="small"
                            disabled
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <TextField
                            label="Description"
                            value={item.serviceDescription}
                            onChange={(e) => updateEditServiceItem(item.id, 'serviceDescription', e.target.value)}
                            fullWidth
                            size="small"
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                ))
              )}

              {/* Cost Summary */}
              <Grid size={{ xs: 12 }}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'var(--primary-color)', mb: 2, pb: 1, borderBottom: '1px solid #e0e0e0' }}>
                  Cost Summary
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label="Parts Cost (₹)"
                  value={editServiceItems.reduce((sum, item) => sum + (item.totalPartCost || 0), 0)}
                  fullWidth
                  disabled
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label="Labour Cost (₹)"
                  type="number"
                  value={editingServiceData.labourCost || '0'}
                  onChange={(e) => setEditingServiceData({
                    ...editingServiceData,
                    labourCost: e.target.value
                  })}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label="Total Service Cost (₹)"
                  value={calculateEditTotalCost()}
                  fullWidth
                  disabled
                  sx={{ '& .MuiInputBase-input': { fontWeight: 600, fontSize: '1.1rem' } }}
                />
              </Grid>

              {/* Service Details Section */}
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'var(--primary-color)', mb: 2, pb: 1, borderBottom: '1px solid #e0e0e0', mt: 2 }}>
                  Service Details
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Mechanic Name"
                  value={editingServiceData.mechanicName || ''}
                  onChange={(e) => setEditingServiceData({
                    ...editingServiceData,
                    mechanicName: e.target.value
                  })}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Service Center"
                  value={editingServiceData.serviceCenter || ''}
                  onChange={(e) => setEditingServiceData({
                    ...editingServiceData,
                    serviceCenter: e.target.value
                  })}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Next Service Due (KM)"
                  type="number"
                  value={editingServiceData.nextServiceDueKm || ''}
                  onChange={(e) => setEditingServiceData({
                    ...editingServiceData,
                    nextServiceDueKm: e.target.value
                  })}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <DatePicker
                  label="Next Service Due Date"
                  value={editingServiceData.nextServiceDueDate ? new Date(editingServiceData.nextServiceDueDate) : null}
                  onChange={(date) => setEditingServiceData({
                    ...editingServiceData,
                    nextServiceDueDate: date ? date.toISOString().split('T')[0] : ''
                  })}
                  slotProps={{
                    textField: { fullWidth: true, variant: 'outlined' }
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={editingServiceData.status || 'initiated'}
                    onChange={(e) => setEditingServiceData({
                      ...editingServiceData,
                      status: e.target.value
                    })}
                    label="Status"
                  >
                    <MenuItem value="initiated">Initiated</MenuItem>
                    <MenuItem value="processing">Processing</MenuItem>
                    <MenuItem value="processed">Processed</MenuItem>
                    <MenuItem value="consolidating">Consolidating</MenuItem>
                    <MenuItem value="review">Review</MenuItem>
                    <MenuItem value="payment">Payment</MenuItem>
                    <MenuItem value="closed">Closed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  label="Remarks"
                  multiline
                  rows={2}
                  value={editingServiceData.remarks || ''}
                  onChange={(e) => setEditingServiceData({
                    ...editingServiceData,
                    remarks: e.target.value
                  })}
                  fullWidth
                />
              </Grid>
            </Grid>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
          <Button
            variant="outlined"
            onClick={handleCloseEditModal}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              minWidth: 120,
              borderColor: '#e0e0e0',
              color: '#666',
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveEdit}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              minWidth: 120,
              backgroundColor: 'var(--primary-color)',
            }}
          >
            Update Service
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">
          Confirm Service Deletion
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this service record? This action cannot be undone.
          </Typography>
          {serviceToDelete && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="subtitle2">
                Service: {getServiceTypes(serviceToDelete)}
              </Typography>
              <Typography variant="body2">
                Date: {formatDate(serviceToDelete.spareChangedDate)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ServiceHistoryDetails;
